import { useState, useCallback } from 'react';

/**
 * Drop-in replacement for useState that persists the value in sessionStorage.
 * On mount, reads the stored value (if any); on every set, writes back.
 * Falls back to regular useState if sessionStorage is unavailable.
 */
export function useSessionState<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setStateRaw] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // Ignore parse errors or unavailable storage
    }
    return defaultValue;
  });

  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateRaw((prev) => {
        const next = value instanceof Function ? value(prev) : value;
        try {
          sessionStorage.setItem(key, JSON.stringify(next));
        } catch {
          // Ignore quota or unavailable storage errors
        }
        return next;
      });
    },
    [key],
  );

  return [state, setState];
}
